import { useEffect, useRef } from 'react'
import {
	data,
	Links,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { SeoHead } from '#app/seo/seo-head.tsx'
import { type Route } from './+types/root.ts'
import { anonIdMiddleware } from './middleware.server.ts'
import {
	initAnalyticsClient,
	identifyClient,
	resetClient,
	trackClient,
	type PageType,
} from './analytics/index.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { ConfettiComponent } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	useOptionalTheme,
	useTheme,
} from './routes/resources+/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getConfetti } from './utils/confetti.server.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		/*
		 * Only the latin subset is preloaded. It is the one every page needs
		 * for the wordmark and H1; latin-ext and vietnamese are fetched on
		 * demand by `unicode-range` and preloading them would download two
		 * files most pages never draw a glyph from.
		 *
		 * `crossOrigin` is required even though this is same-origin: fonts are
		 * always fetched in CORS mode, and a preload whose mode does not match
		 * the real request is discarded and fetched a second time.
		 */
		{
			rel: 'preload',
			href: '/fonts/fraunces-latin.woff2',
			as: 'font',
			type: 'font/woff2',
			crossOrigin: 'anonymous',
		} as const,
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

/**
 * Runs before every loader in the app, which is the only place the visitor's
 * anonymous id can be established such that sibling loaders can read it on the
 * same request. See `anonIdMiddleware`.
 */
export const middleware = [anonIdMiddleware]

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							email: true,
							billingAddress: {
								select: {
									country: true,
									state: true,
									postalCode: true,
								},
							},
							//image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
							subscriptions: {
								select: {
									id: true,
									status: true,
									currentPeriodEnd: true,
									cancelAtPeriodEnd: true,
									plan: {
										select: {
											name: true,
										},
									},
								},
								where: {
									status: {
										in: ['active', 'trialing', 'past_due'], // Only active-ish subscriptions
									},
								},
								orderBy: {
									currentPeriodEnd: 'desc', // Most recent first
								},
								take: 1, // Only need the most recent active subscription
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const honeyProps = await honeypot.getInputProps()

	const generalSettings = await time(
		() =>
			prisma.generalSettings.findUnique({
				where: { id: 'dfwpros' },
			}),
		{
			timings,
			type: 'get general settings',
			desc: 'get general settings in root',
		},
	)

	return data(
		{
			user,
			generalSettings,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			confettiId,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				confettiHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html
			lang="en"
			className={`${theme} h-full overflow-x-hidden scroll-smooth`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{/* Centralized SEO (last matching route wins) */}
				<SeoHead />
				{env.GOOGLE_ADS_ID || env.GOOGLE_ANALYTICS_ID ? (
					<>
						<script
							nonce={nonce}
							async
							src={`https://www.googletagmanager.com/gtag/js?id=${
								env.GOOGLE_ANALYTICS_ID ?? env.GOOGLE_ADS_ID
							}`}
						/>
						{/*
						 * Both tags are configured once, here. `send_page_view: false`
						 * on GA4 because this is a SPA — GtagClient sends an explicit
						 * page_view per navigation, including the first one.
						 */}
						<script
							nonce={nonce}
							dangerouslySetInnerHTML={{
								__html: [
									`window.dataLayer = window.dataLayer || [];`,
									`function gtag(){dataLayer.push(arguments);}`,
									`gtag('js', new Date());`,
									env.GOOGLE_ANALYTICS_ID
										? `gtag('config', '${env.GOOGLE_ANALYTICS_ID}', { send_page_view: false });`
										: '',
									env.GOOGLE_ADS_ID
										? `gtag('config', '${env.GOOGLE_ADS_ID}', { send_page_view: false });`
										: '',
								]
									.filter(Boolean)
									.join('\n'),
							}}
						/>
					</>
				) : null}
			</head>
			<body className="bg-background text-foreground data-[scroll-locked]:!overflow-visible">
				{children}
				<script
					nonce={nonce}
					suppressHydrationWarning
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document nonce={nonce} theme={theme} env={data?.ENV}>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()

	useToast(data.toast)

	return (
		<>
			<div className="flex min-h-screen flex-col justify-between">
				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>
			</div>
			<EpicToaster richColors position="bottom-right" theme={theme} />
			<EpicProgress />
			<PostHogClient />
			<GtagClient />
			<ConfettiComponent id={data.confettiId} />
		</>
	)
}

/**
 * Reads analytics context off the matched route tree.
 *
 * `route_id` is the React Router route id (e.g. `routes/_marketing+/best+/
 * $categorySlug`) rather than the raw pathname, so every DFW city collapses into
 * one row instead of 55. `page_type` comes from an optional
 * `handle.analytics.pageType` on the route, which is what lets us compare the
 * page archetypes in `docs/directory-rebuild-plan.md` against each other.
 */
function useRouteAnalytics() {
	const matches = useMatches()
	const leaf = matches[matches.length - 1]
	const handle = leaf?.handle as
		| { analytics?: { pageType?: PageType } }
		| undefined

	return {
		routeId: leaf?.id,
		pageType: handle?.analytics?.pageType,
	}
}

function PostHogClient() {
	const { user } = useLoaderData<typeof loader>()
	const location = useLocation()
	const { routeId, pageType } = useRouteAnalytics()

	// Initialize PostHog on mount
	useEffect(() => {
		initAnalyticsClient()
	}, [])

	// Handle user identification/reset.
	//
	// `resetClient()` must only run on an actual sign-out transition. Calling it
	// for every anonymous visitor — which is what running it on mount did — issues
	// a brand new distinct_id on every page load, so an anonymous visitor looks
	// like a new person on each request and no multi-page funnel can ever
	// complete. On a directory, anonymous traffic is essentially all traffic.
	const previousUserId = useRef<string | null>(null)

	useEffect(() => {
		if (user?.id) {
			// Determine if user is paying based on subscription status
			const activeSubscription = user.subscriptions?.[0]
			const isPaying =
				activeSubscription?.status === 'active' ||
				activeSubscription?.status === 'trialing'

			identifyClient(user.id, {
				email: user.email ?? undefined,
				plan:
					activeSubscription?.plan?.name ||
					user.roles?.map((r) => r.name).join(',') ||
					undefined,
				is_paying: isPaying,
			})
			previousUserId.current = user.id
		} else if (previousUserId.current) {
			// Was identified, now is not: a real sign-out.
			resetClient()
			previousUserId.current = null
		}
	}, [user?.id, user?.email, user?.roles, user?.subscriptions])

	// Track pageviews.
	//
	// `capture_pageview` is off in the PostHog init, so this is the only place a
	// pageview originates. Keyed on `location.key` because that is the value
	// React Router changes on every navigation — including a repeat navigation to
	// the same URL, which pathname alone would swallow.
	useEffect(() => {
		trackClient('$pageview', {
			// PostHog reads the URL off the document by default, which on a
			// client-side navigation is still correct here because the effect runs
			// after the DOM update. Sending them explicitly removes the ambiguity.
			$current_url: window.location.href,
			$pathname: location.pathname,
			route_id: routeId,
			page_type: pageType,
			// Search params are part of the page identity on a directory: a facet
			// filter is a different page for conversion purposes.
			search: location.search || undefined,
		})
	}, [location.key, location.pathname, location.search, routeId, pageType])

	return null
}

/**
 * Google tag. Handles the Ads remarketing/conversion tag and, when configured,
 * GA4 — which is kept only for the Search Console join (query -> landing page).
 * Product decisions come from PostHog.
 */
function GtagClient() {
	const location = useLocation()

	useEffect(() => {
		if (typeof window === 'undefined') return
		if (!ENV.GOOGLE_ANALYTICS_ID) return

		// A GA4 SPA pageview is an explicit `page_view` event. Re-calling
		// `gtag('config', ...)` on every navigation — which is what this used to
		// do — re-initializes the tag and double-counts, because `config`
		// sends its own page_view unless told not to.
		//
		// gtag/js loads async. Bailing out when `window.gtag` is missing would
		// silently drop the landing pageview — the one hit carrying the entry URL
		// and referrer, and the only one the Search Console join really cares
		// about. So fall back to queueing on `dataLayer` directly, which gtag/js
		// drains when it finishes loading.
		//
		// The queued entry has to be an `arguments` object, not an array, which is
		// why this goes through a function rather than pushing a literal.
		const w = window as any
		w.dataLayer = w.dataLayer || []
		function queueGtag(..._args: any[]) {
			w.dataLayer.push(arguments)
		}

		const send = typeof w.gtag === 'function' ? w.gtag : queueGtag
		send('event', 'page_view', {
			send_to: ENV.GOOGLE_ANALYTICS_ID,
			page_location: window.location.href,
			page_path: location.pathname + location.search,
			page_title: document.title,
		})
	}, [location.key, location.pathname, location.search])

	return null
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
